/**
 * @file theme-limits.ts
 * @description Пределы чисел оформления сайта v2 (phase19.16, партия SB120
 * «свобода выбора», этап 2 «оформление сайта числами»; site-builder.md SB120;
 * проект данных docs/phases/phase19.16-materials/db-design-sb120-stage2.md §1,
 * проект UI uiux-sb120-stage2.md §5).
 *
 * Один источник для схемы темы (schema.ts), подъёма v1 → v2 (legacy-v1.ts),
 * CSS (css.ts) и ползунков кабинета (`NumberSliderField` — min/max/шаг).
 * Пределы — то, что платформа держит жёстко; вкус владельца — предупреждения
 * кабинета, а не запреты (образец — site-section/look-limits.ts этапа 1).
 *
 * Единицы: px — пиксели ПК; em — доли кегля; % — проценты; множители — без
 * единиц. `legacy` — значение, в которое переходит прежнее «включено»
 * (тень 50 % = прежняя тень, свечение 18 % = прежняя дымка).
 */

/** Пределы оформления сайта. */
export const SITE_THEME_LIMITS = {
  /** Множитель кегля H1–H3 (`--site-heading-scale`); шаг 0,025 — чтобы 0,875 и 1,125 ложились на шаг. */
  headingScale: { min: 0.75, max: 1.5, step: 0.025, default: 1 },
  /** Разрядка надписей капсом, em (`--site-eyebrow-tracking`). */
  eyebrowTracking: { min: 0, max: 0.4, step: 0.01, default: 0.08 },
  /** Скругление кнопок, px; «Пилюля» — отдельное значение `"pill"`. */
  buttonRadius: { min: 0, max: 40 },
  /** Скругление карточек и фото (`card`) и полей и бейджей (`field`), px. */
  radius: { min: 0, max: 40 },
  /** Толщина рамки карточек, целые px (0 — без рамки). */
  cardBorderWidth: { min: 0, max: 4 },
  /** Тень карточек, % (50 — прежняя тень «С тенью», `SITE_CARD_SHADOW`). */
  cardShadow: { min: 0, max: 100, legacy: 50 },
  /** Сила свечения (бывшая «дымка»), % прозрачности цвета (18 — прежняя дымка). */
  hazeStrength: { min: 0, max: 50, legacy: 18 },
  /** Место рисунка фона, % области рисунков по x и y. */
  backdropPosition: { min: 0, max: 100 },
  /** Ширина рисунка фона, px (ПК, планшет, телефон). */
  backdropSize: { min: 40, max: 600 },
  /**
   * Яркость рисунка фона, %. Нет ключа — как было: 30 на телефоне, 35 с
   * 768 px (SiteBackdrop.tsx `opacity-30 md:opacity-35`).
   */
  backdropOpacity: { min: 0, max: 100, phone: 30, wide: 35 },
  /** Ширина рамки сайта «По центру», px. */
  frameMaxWidth: { min: 960, max: 1920, default: 1440 },
  /** Тень рамки сайта, % (50 — прежняя тень рамки). */
  frameShadow: { min: 0, max: 100, legacy: 50 },
  /** Плотность отступов сайта — множитель шкалы `--site-space-*`. */
  density: { min: 0.5, max: 1.6, default: 1 },
  /**
   * Вид корзины (SB120 этап 4, db-design-sb120-stage4 §1.3), целые числа.
   * `warnAbove` / `warnBelow` — порог предупреждения кабинета (не запрет,
   * `cartLookWarnings`); умолчания — нынешняя панель CartDrawer.tsx.
   */
  cart: {
    /**
     * Ширина панели до 640 px экрана, px. 288 = 80 % от 360; `keepMin` —
     * ширина, которую витрина держит и на узком экране; `freeMin` — сколько
     * страницы остаётся видно сбоку (предел «экран − 48», uiux-cart §4).
     */
    widthPhone: { min: 240, max: 360, default: 300, warnAbove: 288, keepMin: 300, freeMin: 48 },
    /** Ширина панели от 640 px экрана, px. */
    widthWide: { min: 320, max: 640, default: 448, warnAbove: 560 },
    /** Ширина окна из шапки, px. */
    popoverWidth: { min: 320, max: 480, default: 400 },
    /** Строк в окне до прокрутки. */
    popoverRows: { min: 2, max: 8, default: 4, warnAbove: 6 },
    /** Затемнение под корзиной, % (как SB119 «0–80 %»); умолчание у панели и у окна своё. */
    dim: { min: 0, max: 80, drawer: 70, popover: 0 },
    /** Скругление фото и «Итого», px; у кнопок −/+ — доля `controlShare` (8 → 6). */
    radius: { min: 0, max: 24, default: 8, controlShare: 0.75 },
    /** Размер фото товара в строке, px. */
    photo: { min: 48, max: 120, default: 80, warnBelow: 56 },
    /** С какой ширины экрана — «от 640 px» (Tailwind sm). */
    wideFrom: 640,
  },
  /**
   * Баннер согласия на cookie (phase19.16 Р3г, SB134; db-design-r3g §2.3):
   * ширина карточки в углу на ПК — число, а не ступень (владелец рисует сам);
   * на экране уже `wideFrom` баннер всегда полоса.
   */
  consentBanner: {
    cardWidth: { min: 320, max: 480, default: 420 },
    wideFrom: 640,
  },
  /**
   * Свои строки блока «Ссылки» подвала (phase19.16 SB142, раздел
   * `theme.footerDocs`, footer-docs.ts): `max` — жёсткий предел схемы,
   * `warnAbove` — мягкое предупреждение после стольких включённых строк.
   */
  footerDocs: { max: 12, warnAbove: 3 },
} as const;

/**
 * Область рисунков фона: окно без полосы сверху (под шапку) и снизу, px.
 * Источник — прежние углы SiteBackdrop.tsx (`top-24` = 96 px, `bottom-12` =
 * 48 px): точка 0/0 и 100/100 совпадают с прежними углами.
 */
export const SITE_BACKDROP_AREA = { top: 96, bottom: 48 } as const;

/**
 * Ширины устройств для рисунков фона, px: планшет — с 768, ПК — с 1024
 * (прежние `md:` и `lg:` SiteBackdrop.tsx). «ПК» у рисунков — от 1024
 * (db-design-sb120-stage2, расхождение 3).
 */
export const SITE_BACKDROP_BREAKPOINTS = { tablet: 768, desktop: 1024 } as const;

/**
 * Доли ширины рисунка на планшете и телефоне от ПК, когда своего числа нет
 * (`sizeTablet` / `sizePhone`). Взяты у прежнего размера M: 320 → 220 / 140.
 */
export const SITE_BACKDROP_SIZE_SHARE = { tablet: 0.6875, phone: 0.4375 } as const;
